<template>
  <div class="billing-page">
    <!-- Header -->
    <div class="billing-header">
      <div>
        <h1 class="page-title">Facturation</h1>
        <p class="page-subtitle">Gérez vos abonnements et factures</p>
      </div>
      <div class="header-actions-group">
            <Button 
              v-if="subscriptions.length > 0"
              label="Payer maintenant" 
              icon="pi pi-credit-card"
              class="p-button-success"
              @click="openPaddleCheckout"
              :disabled="!paddleReady"
            />
        <Button 
          label="Nouvelle facture" 
          icon="pi pi-plus"
          class="p-button-primary"
          @click="showNewInvoiceDialog = true"
        />
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon" style="background: rgba(59, 130, 246, 0.1); color: #3b82f6;">
          <i class="pi pi-wifi"></i>
        </div>
        <div class="stat-content">
          <p class="stat-label">Capteurs actifs</p>
          <h3 class="stat-value">{{ sensorCount }}</h3>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background: rgba(139, 92, 246, 0.1); color: #8b5cf6;">
          <i class="pi pi-money-bill"></i>
        </div>
        <div class="stat-content">
          <p class="stat-label">Prix unitaire</p>
          <h3 class="stat-value">2 000 MAD</h3>
        </div>
      </div>
      <div class="stat-card highlight">
        <div class="stat-icon" style="background: rgba(16, 185, 129, 0.1); color: #10b981;">
          <i class="pi pi-calculator"></i>
        </div>
        <div class="stat-content">
          <p class="stat-label">Total annuel</p>
          <h3 class="stat-value highlight-value">{{ calculateAnnualPrice(sensorCount).toLocaleString('fr-FR') }} MAD</h3>
        </div>
      </div>
    </div>

    <!-- Subscription Card -->
    <Card class="subscription-card" v-if="subscriptions.length > 0">
      <template #header>
        <div class="card-header">
          <div>
            <h3 class="card-title">Abonnement actif</h3>
            <p class="card-subtitle">Votre plan d'abonnement actuel</p>
          </div>
          <div class="card-header-right">
            <span class="status-badge status-active">
              <i class="pi pi-check-circle"></i>
              {{ getStatusLabel(subscriptions[0].status) }}
            </span>
            <Button 
              v-if="paddleReady"
              label="Payer" 
              icon="pi pi-credit-card"
              class="p-button-success p-button-sm"
              @click="openPaddleCheckout"
              style="margin-left: 0.75rem;"
            />
          </div>
        </div>
      </template>
      <template #content>
        <div class="subscription-details">
          <div class="subscription-main">
            <div class="subscription-info">
              <div class="subscription-plan-name">
                <h4>{{ subscriptions[0].sensorCount }} capteur{{ subscriptions[0].sensorCount > 1 ? 's' : '' }}</h4>
                <p class="subscription-plan-type">Abonnement annuel</p>
              </div>
              <div class="subscription-price-section">
                <div class="price-main">
                  <span class="price-amount">{{ subscriptions[0].price.toLocaleString('fr-FR') }}</span>
                  <span class="price-currency">MAD</span>
                </div>
                <p class="price-period">par an</p>
              </div>
            </div>
            <div class="subscription-dates-info">
              <div class="date-item">
                <i class="pi pi-calendar"></i>
                <div>
                  <span class="date-label">Début</span>
                  <span class="date-value">{{ formatDateShort(subscriptions[0].startDate) }}</span>
                </div>
              </div>
              <div class="date-item">
                <i class="pi pi-calendar-times"></i>
                <div>
                  <span class="date-label">Fin</span>
                  <span class="date-value">{{ formatDateShort(subscriptions[0].endDate) }}</span>
                </div>
              </div>
              <div class="date-item">
                <i class="pi pi-credit-card"></i>
                <div>
                  <span class="date-label">Prochain paiement</span>
                  <span class="date-value">{{ formatDateShort(subscriptions[0].nextBillingDate) }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="subscription-actions">
            <Button 
              label="Payer avec Paddle" 
              icon="pi pi-credit-card"
              class="p-button-primary"
              @click="openPaddleCheckout"
              :disabled="!paddleReady"
            />
            <div v-if="!paddleReady" class="paddle-status-info">
              <i class="pi pi-info-circle"></i>
              <div class="paddle-status-details">
                <span><strong>Paddle n'est pas initialisé</strong></span>
                <span class="paddle-status-reasons">
                  <span v-if="!hasVendorId">❌ VUE_APP_PADDLE_TOKEN ou VUE_APP_PADDLE_SELLER_ID manquant</span>
                  <span v-else-if="!paddleScriptLoaded">⏳ Chargement du script Paddle...</span>
                  <span v-else>⚠️ Erreur d'initialisation - Vérifiez la console</span>
                </span>
                <button class="paddle-retry-btn" @click="initializePaddle">
                  <i class="pi pi-refresh"></i> Réessayer
                </button>
              </div>
            </div>
            <Button 
              label="Gérer l'abonnement" 
              icon="pi pi-cog"
              class="p-button-outlined"
              @click="manageSubscription(subscriptions[0].id)"
            />
            <Button 
              label="Annuler" 
              icon="pi pi-times"
              class="p-button-text p-button-danger"
              @click="cancelSubscription(subscriptions[0].id)"
            />
          </div>
        </div>
      </template>
    </Card>

    <!-- Invoices Table -->
    <Card class="invoices-card">
      <template #header>
        <div class="card-header">
          <div>
            <h3 class="card-title">Factures</h3>
            <p class="card-subtitle">Historique de vos factures</p>
          </div>
          <div class="header-actions">
            <Button 
              icon="pi pi-download" 
              class="p-button-text"
              @click="exportInvoices"
              title="Exporter"
            />
            <Button 
              icon="pi pi-filter" 
              class="p-button-text"
              @click="showFilters = !showFilters"
              title="Filtrer"
            />
          </div>
        </div>
      </template>
      <template #content>
        <div v-if="loading" class="loading-container">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Chargement...</span>
        </div>
        <div v-else-if="invoices.length === 0" class="empty-state">
          <i class="pi pi-file"></i>
          <h4>Aucune facture</h4>
          <p>Vous n'avez pas encore de factures</p>
        </div>
        <div v-else class="invoices-table-container">
          <table class="invoices-table">
            <thead>
              <tr>
                <th>Facture</th>
                <th>Date</th>
                <th>Montant</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="invoice in invoices" :key="invoice.id">
                <td>
                  <div class="invoice-number-cell">
                    <strong>#{{ invoice.number }}</strong>
                    <span v-if="invoice.description" class="invoice-description">{{ invoice.description }}</span>
                  </div>
                </td>
                <td>
                  <span class="invoice-date">{{ formatDateShort(invoice.date) }}</span>
                </td>
                <td>
                  <span class="invoice-amount">{{ invoice.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) }} MAD</span>
                </td>
                <td>
                  <span class="status-badge" :class="`status-${invoice.status}`">
                    <i :class="getStatusIcon(invoice.status)"></i>
                    {{ getStatusLabel(invoice.status) }}
                  </span>
                </td>
                <td>
                  <div class="table-actions">
                    <Button 
                      icon="pi pi-eye" 
                      class="p-button-text p-button-sm"
                      @click="viewInvoiceDetails(invoice.id)"
                      title="Voir détails"
                    />
                    <Button 
                      icon="pi pi-download" 
                      class="p-button-text p-button-sm"
                      @click="downloadInvoice(invoice.id)"
                      title="Télécharger"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import Button from 'primevue/button';
import { nodeService } from '@/services/api/nodeService';
import { paddleService } from '@/services/paddleService';
import { useUser } from '@/composables/useUser';

export default {
  name: 'Facturation',
  components: { Card, Button },
  setup() {
    const toast = useToast();
    const { user, fetchUser } = useUser();
    const loading = ref(false);
    const invoices = ref([]);
    const subscriptions = ref([]);
    const sensorCount = ref(0);
    const showNewInvoiceDialog = ref(false);
    const showFilters = ref(false);
    const paddleReady = ref(false);
    const hasVendorId = ref(false);
    const paddleScriptLoaded = ref(false);

    // Prix par capteur par an (en MAD)
    const PRICE_PER_SENSOR_PER_YEAR = 2000;

    // Calculate annual subscription price based on sensor count
    const calculateAnnualPrice = (count) => {
      return count * PRICE_PER_SENSOR_PER_YEAR;
    };

    // Format date short
    const formatDateShort = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    // Get status label
    const getStatusLabel = (status) => {
      const labels = {
        'active': 'Actif',
        'inactive': 'Inactif',
        'cancelled': 'Annulé',
        'expired': 'Expiré',
        'pending': 'En attente',
        'paid': 'Payé',
        'unpaid': 'Impayé',
        'overdue': 'En retard'
      };
      return labels[status] || status;
    };

    // Get status icon
    const getStatusIcon = (status) => {
      const icons = {
        'active': 'pi pi-check-circle',
        'paid': 'pi pi-check-circle',
        'pending': 'pi pi-clock',
        'unpaid': 'pi pi-exclamation-circle',
        'overdue': 'pi pi-exclamation-triangle',
        'cancelled': 'pi pi-times-circle',
        'expired': 'pi pi-times-circle'
      };
      return icons[status] || 'pi pi-circle';
    };

    // Fetch invoices
    const fetchInvoices = async () => {
      try {
        const annualPrice = calculateAnnualPrice(sensorCount.value);
        if (annualPrice > 0) {
          const currentYear = new Date().getFullYear();
          invoices.value = [
            {
              id: 1,
              number: `INV-${currentYear}-001`,
              date: `${currentYear}-01-15`,
              amount: annualPrice,
              status: 'paid',
              description: `Abonnement annuel - ${sensorCount.value} capteur${sensorCount.value > 1 ? 's' : ''}`
            }
          ];
        } else {
          invoices.value = [];
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
        toast.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger les factures',
          life: 3000
        });
      }
    };

    // Fetch sensors count
    const fetchSensors = async () => {
      try {
        const response = await nodeService.getNodes();
        const nodes = response?.data || response || [];
        sensorCount.value = Array.isArray(nodes) ? nodes.length : 0;
        updateSubscriptionsWithSensorPrice();
      } catch (error) {
        console.error('Error fetching sensors:', error);
        toast.add({
          severity: 'warn',
          summary: 'Avertissement',
          detail: 'Impossible de charger le nombre de capteurs',
          life: 3000
        });
      }
    };

    // Update subscriptions with calculated price based on sensor count
    const updateSubscriptionsWithSensorPrice = () => {
      if (sensorCount.value > 0) {
        const annualPrice = calculateAnnualPrice(sensorCount.value);
        const currentYear = new Date().getFullYear();
        const startDate = new Date(currentYear, 0, 1);
        const endDate = new Date(currentYear, 11, 31);
        const nextBillingDate = new Date(currentYear + 1, 0, 1);
        
        subscriptions.value = [
          {
            id: 1,
            name: `Abonnement Annuel - ${sensorCount.value} capteur${sensorCount.value > 1 ? 's' : ''}`,
            plan: 'Abonnement Annuel',
            price: annualPrice,
            period: 'an',
            status: 'active',
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            nextBillingDate: nextBillingDate.toISOString().split('T')[0],
            sensorCount: sensorCount.value
          }
        ];
      } else {
        subscriptions.value = [];
      }
    };

    // Fetch subscriptions
    const fetchSubscriptions = async () => {
      loading.value = true;
      try {
        await fetchSensors();
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
        toast.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger les abonnements',
          life: 3000
        });
      } finally {
        loading.value = false;
      }
    };

    // Download invoice
    const downloadInvoice = (invoiceId) => {
      toast.add({
        severity: 'info',
        summary: 'Téléchargement',
        detail: `Téléchargement de la facture #${invoiceId}...`,
        life: 3000
      });
    };

    // View invoice details
    const viewInvoiceDetails = (invoiceId) => {
      toast.add({
        severity: 'info',
        summary: 'Détails',
        detail: `Affichage des détails de la facture #${invoiceId}`,
        life: 3000
      });
    };

    // Manage subscription
    const manageSubscription = (subscriptionId) => {
      toast.add({
        severity: 'info',
        summary: 'Gestion',
        detail: `Gestion de l'abonnement #${subscriptionId}`,
        life: 3000
      });
    };

    // Cancel subscription
    const cancelSubscription = (subscriptionId) => {
      toast.add({
        severity: 'warn',
        summary: 'Annulation',
        detail: `Annulation de l'abonnement #${subscriptionId}`,
        life: 3000
      });
    };

    // Export invoices
    const exportInvoices = () => {
      toast.add({
        severity: 'info',
        summary: 'Export',
        detail: 'Export des factures...',
        life: 3000
      });
    };

    // Initialize Paddle
    const initializePaddle = async () => {
      try {
        // Check for token, seller ID, or vendor ID (in priority order)
        const token = process.env.VUE_APP_PADDLE_TOKEN;
        const sellerId = process.env.VUE_APP_PADDLE_SELLER_ID || process.env.VUE_APP_PADDLE_VENDOR_ID;
        const environment = process.env.VUE_APP_PADDLE_ENVIRONMENT || 'sandbox';
        
        // Check if credentials exist
        hasVendorId.value = !!(token || sellerId);
        
        console.log('🔍 Checking Paddle configuration...', {
          hasToken: !!token,
          token: token ? `${token.substring(0, 10)}...` : 'NOT FOUND',
          sellerId: sellerId ? `${sellerId.substring(0, 3)}...` : 'NOT FOUND',
          environment,
          allEnvVars: Object.keys(process.env).filter(k => k.includes('PADDLE')),
          processEnvKeys: Object.keys(process.env).slice(0, 20) // First 20 keys for debugging
        });
        
        if (!token && !sellerId) {
          console.warn('⚠️ Paddle credentials not found in environment variables');
          console.warn('💡 Make sure you have VUE_APP_PADDLE_TOKEN, VUE_APP_PADDLE_SELLER_ID, or VUE_APP_PADDLE_VENDOR_ID in your .env file');
          console.warn('💡 Restart your dev server after adding .env variables');
          console.warn('💡 Check that the variable starts with VUE_APP_');
          toast.add({
            severity: 'warn',
            summary: 'Paddle non configuré',
            detail: 'VUE_APP_PADDLE_TOKEN ou VUE_APP_PADDLE_SELLER_ID manquant. Vérifiez votre .env et redémarrez le serveur.',
            life: 8000
          });
          paddleReady.value = false;
          paddleScriptLoaded.value = false;
          return;
        }

        if (token) {
          console.log('🔄 Initializing Paddle with token:', `${token.substring(0, 10)}...`);
        } else {
          console.log('🔄 Initializing Paddle with Seller ID:', sellerId);
        }
        console.log('📊 Paddle Service Status:', paddleService.getStatus());
        
        // Check if Paddle script is already loaded
        if (window.Paddle) {
          console.log('✅ Paddle script already loaded');
          paddleScriptLoaded.value = true;
        } else {
          console.log('⏳ Loading Paddle script...');
        }
        
        const paddle = await paddleService.initialize();
        
        // Check if script loaded
        paddleScriptLoaded.value = !!window.Paddle;
        console.log('📊 Paddle Service Status After Init:', paddleService.getStatus());
        
        if (paddle) {
          paddleReady.value = paddleService.isReady();
          
          if (paddleReady.value) {
            console.log('✅ Paddle initialized and ready for checkout');
            console.log('✅ Paddle instance:', paddle);
            console.log('✅ window.Paddle available:', !!window.Paddle);
            toast.add({
              severity: 'success',
              summary: 'Paddle prêt',
              detail: `Paddle ${environment} initialisé avec succès`,
              life: 3000
            });
          } else {
            console.warn('⚠️ Paddle initialized but isReady() returned false');
            console.warn('⚠️ isInitialized:', paddleService.isInitialized);
            console.warn('⚠️ paddleInstance:', paddleService.paddleInstance);
            paddleReady.value = false;
          }
        } else {
          console.error('❌ Paddle initialization returned null');
          console.error('❌ Check if VUE_APP_PADDLE_VENDOR_ID is correct');
          paddleReady.value = false;
        }
      } catch (error) {
        console.error('❌ Failed to initialize Paddle:', error);
        console.error('Error details:', error.stack);
        console.error('Error message:', error.message);
        paddleReady.value = false;
        toast.add({
          severity: 'error',
          summary: 'Erreur Paddle',
          detail: error.message || 'Impossible d\'initialiser Paddle. Vérifiez la console.',
          life: 8000
        });
      }
    };

    // Open Paddle Checkout
    const openPaddleCheckout = async () => {
      try {
        // Try to initialize if not ready
        if (!paddleReady.value) {
          console.log('🔄 Paddle not ready, attempting to initialize...');
          await initializePaddle();
          
          if (!paddleReady.value) {
            toast.add({
              severity: 'error',
              summary: 'Paddle non disponible',
              detail: 'Paddle n\'est pas initialisé. Vérifiez VUE_APP_PADDLE_VENDOR_ID dans .env et redémarrez le serveur.',
              life: 5000
            });
            return;
          }
        }

        if (subscriptions.value.length === 0) {
          toast.add({
            severity: 'warn',
            summary: 'Aucun abonnement',
            detail: 'Aucun abonnement à payer',
            life: 3000
          });
          return;
        }

        const subscription = subscriptions.value[0];
        const annualPrice = subscription.price;
        
        // Convert MAD to cents for Paddle
        const priceInCents = paddleService.convertMADToCents(annualPrice);

        // Get user email
        const customerEmail = user.value?.email || '';

        // Verify Price ID is loaded
        const priceId = process.env.VUE_APP_PADDLE_PRICESID || null;
        const productId = process.env.VUE_APP_PADDLE_PRODUCT_ID || null;
        
        if (!priceId && !productId) {
          toast.add({
            severity: 'warn',
            summary: 'Configuration manquante',
            detail: 'VUE_APP_PADDLE_PRICESID ou VUE_APP_PADDLE_PRODUCT_ID doit être défini dans .env',
            life: 5000
          });
          console.warn('⚠️ No Price ID or Product ID found in environment variables');
          console.warn('💡 Add VUE_APP_PADDLE_PRICESID or VUE_APP_PADDLE_PRODUCT_ID to your .env file');
          return;
        }
        
        console.log('🛒 Preparing checkout with:', {
          priceId: priceId || 'Not set',
          productId: productId || 'Not set',
          priceInCents,
          priceInMAD: annualPrice,
          email: customerEmail || 'Not provided',
          sensorCount: sensorCount.value
        });
        
        // If using Price ID, don't pass price override (Price ID has its own price)
        // If using Product ID, we can override the price
        const shouldOverridePrice = !priceId && productId;

        await paddleService.openCheckout({
          // Priority: Use Price ID (preferred for Paddle Billing)
          // Note: When using Price ID, don't override price - the Price ID has its own fixed price
          priceId: priceId,
          // Fallback: Use Product ID
          productId: productId,
          // Price override: Only use if we have Product ID (not Price ID)
          // Price ID already has a fixed price, so override would cause conflicts
          price: shouldOverridePrice ? priceInCents : undefined,
          email: customerEmail || undefined, // Only include if email exists
          successUrl: `${window.location.origin}/#/facturation?payment=success`,
          customData: {
            userId: user.value?.id || null,
            sensorCount: sensorCount.value,
            subscriptionId: subscription.id,
            amount: annualPrice,
            currency: 'MAD'
          }
        });

        toast.add({
          severity: 'info',
          summary: 'Checkout Paddle',
          detail: 'Ouverture du checkout Paddle...',
          life: 2000
        });
      } catch (error) {
        console.error('❌ Error opening Paddle checkout:', error);
        console.error('❌ Error name:', error.name);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error stack:', error.stack);
        
        const errorMessage = error.message || 'Impossible d\'ouvrir le checkout Paddle';
        
        toast.add({
          severity: 'error',
          summary: 'Erreur de checkout',
          detail: errorMessage,
          life: 8000
        });
        
        // Update paddle status
        paddleStatusSeverity.value = 'error';
        paddleStatusMessage.value = `❌ Erreur: ${errorMessage}`;
      }
    };

    // Listen to Paddle events
    const setupPaddleListeners = () => {
      window.addEventListener('paddle:checkout:success', (event) => {
        console.log('✅ Paddle checkout success:', event.detail);
        toast.add({
          severity: 'success',
          summary: 'Paiement réussi',
          detail: 'Votre paiement a été traité avec succès',
          life: 5000
        });
        // Refresh subscriptions and invoices
        fetchSubscriptions();
        fetchInvoices();
      });

      window.addEventListener('paddle:checkout:close', () => {
        console.log('❌ Paddle checkout closed by user');
      });

      window.addEventListener('paddle:checkout:error', (event) => {
        console.error('❌ Paddle checkout error event:', event.detail);
        const errorDetail = event.detail || {};
        const errorMessage = errorDetail.message || errorDetail.error || 'Une erreur est survenue lors de l\'ouverture du checkout Paddle';
        
        toast.add({
          severity: 'error',
          summary: 'Erreur de paiement Paddle',
          detail: errorMessage,
          life: 8000
        });
        
        // Update paddle status
        paddleStatusSeverity.value = 'error';
        paddleStatusMessage.value = `❌ Erreur: ${errorMessage}`;
      });
    };

    onMounted(async () => {
      // Fetch user data first
      await fetchUser();
      await fetchSubscriptions();
      await fetchInvoices();
      await initializePaddle();
      setupPaddleListeners();
    });

    return {
      loading,
      invoices,
      subscriptions,
      sensorCount,
      showNewInvoiceDialog,
      showFilters,
      paddleReady,
      hasVendorId,
      paddleScriptLoaded,
      formatDateShort,
      getStatusLabel,
      getStatusIcon,
      calculateAnnualPrice,
      downloadInvoice,
      viewInvoiceDetails,
      manageSubscription,
      cancelSubscription,
      exportInvoices,
      openPaddleCheckout,
      initializePaddle
    };
  }
};
</script>

<style scoped>
.billing-page {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  background: #f9fafb;
  min-height: 100vh;
}

/* Header */
.billing-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 1rem;
}

.header-actions-group {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;
}

.page-subtitle {
  font-size: 0.9375rem;
  color: #6b7280;
  margin: 0;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.stat-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.stat-card.highlight {
  border-color: #10b981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.02) 0%, rgba(5, 150, 105, 0.02) 100%);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0 0 0.25rem 0;
  font-weight: 500;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.02em;
}

.stat-value.highlight-value {
  color: #10b981;
}

/* Cards */
.subscription-card,
.invoices-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
  overflow: hidden;
}

.subscription-card :deep(.p-card-header),
.invoices-card :deep(.p-card-header) {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fafafa;
}

.subscription-card :deep(.p-card-body),
.invoices-card :deep(.p-card-body) {
  padding: 1.5rem;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.card-header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.card-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
}

.status-badge i {
  font-size: 0.875rem;
}

.status-badge.status-active,
.status-badge.status-paid {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.status-badge.status-pending {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.status-badge.status-unpaid,
.status-badge.status-overdue {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.status-badge.status-cancelled,
.status-badge.status-expired {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

/* Subscription Details */
.subscription-details {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.subscription-main {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.subscription-info {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.subscription-plan-name h4 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.subscription-plan-type {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.subscription-price-section {
  text-align: right;
}

.price-main {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
}

.price-amount {
  font-size: 2rem;
  font-weight: 700;
  color: #10b981;
  letter-spacing: -0.02em;
}

.price-currency {
  font-size: 1.25rem;
  font-weight: 600;
  color: #6b7280;
}

.price-period {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.subscription-dates-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.date-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.date-item i {
  font-size: 1.125rem;
  color: #6b7280;
}

.date-item div {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.date-label {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.date-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.subscription-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.paddle-status-info {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #f59e0b;
}

.paddle-status-info i {
  font-size: 1rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.paddle-status-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.paddle-status-reasons {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.75rem;
  opacity: 0.9;
}

.paddle-retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.25rem;
}

.paddle-retry-btn:hover {
  background: #d97706;
  transform: translateY(-1px);
}

.paddle-retry-btn i {
  font-size: 0.875rem;
}

/* Invoices Table */
.invoices-table-container {
  overflow-x: auto;
}

.invoices-table {
  width: 100%;
  border-collapse: collapse;
}

.invoices-table thead {
  background: #f9fafb;
}

.invoices-table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e5e7eb;
}

.invoices-table td {
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
}

.invoices-table tbody tr {
  transition: background 0.2s ease;
}

.invoices-table tbody tr:hover {
  background: #f9fafb;
}

.invoice-number-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.invoice-number-cell strong {
  font-size: 0.9375rem;
  color: #111827;
  font-weight: 600;
}

.invoice-description {
  font-size: 0.8125rem;
  color: #6b7280;
}

.invoice-date {
  font-size: 0.875rem;
  color: #374151;
}

.invoice-amount {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
}

.table-actions {
  display: flex;
  gap: 0.25rem;
}

.p-button-sm {
  padding: 0.5rem !important;
  width: 2rem !important;
  height: 2rem !important;
}

/* Loading & Empty States */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1rem;
  color: #6b7280;
}

.loading-container i {
  font-size: 2rem;
  color: #4caf50;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .billing-page {
    padding: 1rem;
  }

  .billing-header {
    flex-direction: column;
    align-items: stretch;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .subscription-info {
    flex-direction: column;
    gap: 1rem;
  }

  .subscription-price-section {
    text-align: left;
  }

  .subscription-dates-info {
    grid-template-columns: 1fr;
  }

  .subscription-actions {
    flex-direction: column;
  }

  .invoices-table {
    font-size: 0.875rem;
  }

  .invoices-table th,
  .invoices-table td {
    padding: 0.75rem 0.5rem;
  }
}
</style>
